import { Dispatch, SetStateAction, useMemo, useState } from "react"
import { ICartItem } from "../../Interfaces/ICardItem"
import { EAppView } from "../../Enum/EAppViewType"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { CheckoutPupop } from "./CheckoutPupop"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { CalculateCartSummary, getItemTotal } from "../Helpers/CalculateCartSummary"
import { ResolveSelectionOptionPriceLabel } from "../Helpers/ResolveSelectionOptionPriceLabel"
import { AwardAppliedList } from "../Components/AwardAppliedCard"
import { useTranslation } from "react-i18next"
import { companyMemory } from "../../Objects/Memory/Memory"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import ModalPortal from "../../components/Modal/ModalPortal"
import { AddProductRequestPupop } from "./AddProductRequestPupop"
import { IAddtionalRequest } from "../../Interfaces/IAddtionalRequest"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { ISelectedSelectionRequest } from "../../Interfaces/ISelectionRequest"
import { upsertCartItem } from "../Helpers/CartItemHelpers"
import type { TFunction } from "i18next"
import { X } from "lucide-react"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

const buildSelectionStatusMessages = (
  selection: NonNullable<IProductRequest["availableSelections"]>[number],
  selectedItems: ISelectedSelectionRequest[],
  t: TFunction,
) => {
  const selectedCount = selectedItems.filter((item) => item.selectionId === selection.id).length

  const requiredOptionsCount = Math.max(1, selection.requiredOptionsCount ?? 1)

  const freeOptionsLimit = Math.max(0, selection.freeOptionsLimit ?? 0)

  const freeUsed = selectedItems.filter(
    (item) => item.selectionId === selection.id && !item.isFree && item.value <= 0,
  ).length

  const freeRemaining = Math.max(0, freeOptionsLimit - freeUsed)

  const maximumLimitCandidates = [
    selection.hasMaximumSelectionsLimit ? Math.max(1, selection.maximumSelectionsLimit ?? 1) : undefined,
  ].filter((limit): limit is number => typeof limit === "number")

  const effectiveMaximumSelectionsLimit =
    maximumLimitCandidates.length > 0 ? Math.min(...maximumLimitCandidates) : undefined

  const maximumRemaining =
    effectiveMaximumSelectionsLimit === undefined ? 0 : Math.max(0, effectiveMaximumSelectionsLimit - selectedCount)

  const messages = [
    {
      text: selection.isRequired
        ? t("publicMenu:add_product.selected_required_progress", {
            selected: Math.min(selectedCount, requiredOptionsCount),
            total: requiredOptionsCount,
          })
        : t("publicMenu:add_product.selected_optional_progress", { count: selectedCount }),
      className:
        selection.isRequired && selectedCount >= requiredOptionsCount
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : selection.isRequired
            ? "border-red-200 bg-red-50 text-red-700"
            : "border-sky-200 bg-sky-50 text-sky-700",
    },
  ]

  if (freeOptionsLimit > 0) {
    messages.push({
      text: t("publicMenu:add_product.free_choices_remaining", { count: freeRemaining }),
      className:
        freeRemaining > 0 ? "border-lime-200 bg-lime-50 text-lime-700" : "border-amber-200 bg-amber-50 text-amber-700",
    })
  }

  if (effectiveMaximumSelectionsLimit !== undefined) {
    messages.push(
      maximumRemaining > 0
        ? {
            text: t("publicMenu:add_product.maximum_choices_remaining", { count: maximumRemaining }),
            className: "border-violet-200 bg-violet-50 text-violet-700",
          }
        : {
            text: t("publicMenu:add_product.maximum_choices_reached"),
            className: "border-rose-200 bg-rose-50 text-rose-700",
          },
    )
  }

  return messages
}

interface ShoppingCartPupopProps {
  cart: ICartItem[]
  allOrders: OrderEntity[]
  onClose: () => void
  setCurrentView: (view: EAppView) => void
  setCart: Dispatch<SetStateAction<ICartItem[]>>
  setAllOrders: Dispatch<SetStateAction<OrderEntity[]>>
  servicePointSelfService?: { servicePointId: number; servicePointName: string; canCreateOrders: boolean }
}

export const ShoppingCartPupop: React.FC<ShoppingCartPupopProps> = ({
  cart,
  allOrders,
  onClose,
  setCurrentView,
  setCart,
  setAllOrders,
  servicePointSelfService,
}) => {
  const { auth } = usePublicViewContext()

  const { t } = useTranslation("general")

  const theme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)

  const [editingItem, setEditingItem] = useState<ICartItem | null>(null)

  const [planLimitBlockedMessage, setPlanLimitBlockedMessage] = useState(
    companyMemory.canCreatePublicOrder ? "" : t("ID18"),
  )

  const summary = useMemo(
    () => CalculateCartSummary(cart, servicePointSelfService ? null : auth.customer),
    [cart, auth.customer, servicePointSelfService],
  )

  const removeFromCart = (uniqueId: string) => {
    setCart((prev) => prev.filter((item) => item.uniqueId !== uniqueId))
  }

  const updateCartItemQuantity = (uniqueId: string, delta: number) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.uniqueId === uniqueId) {
          return { ...item, quantity: Math.max(1, item.quantity + delta) }
        }

        return item
      }),
    )
  }

  const handleEditCartItem = (
    product: IProductRequest,
    selectedAddOns: IAddtionalRequest[],
    selectedSelections: ISelectedSelectionRequest[],
    quantity: number,
    notes: string,
    reservationInfo: {
      isReservation: boolean
      reservationDate: string
      reservationTime: string
    },
  ) => {
    if (!editingItem) {
      return
    }

    setCart((prev) =>
      upsertCartItem({
        currentCart: prev,
        product,
        selectedAddOns,
        selectedSelections,
        quantity,
        notes,
        isReservation: reservationInfo.isReservation,
        reservationDate: reservationInfo.reservationDate,
        reservationTime: reservationInfo.reservationTime,
        replaceUniqueId: editingItem.uniqueId,
      }),
    )

    setEditingItem(null)
  }

  const handleCheckoutSuccess = (order: OrderEntity) => {
    const updatedOrders = [order, ...allOrders]

    setAllOrders(updatedOrders)

    if (!servicePointSelfService) {
      localStorage.setItem("orderpro_history", JSON.stringify(updatedOrders))
    }

    setIsCheckoutOpen(false)

    onClose()

    setCart([])

    setCurrentView(EAppView.OrderTracking)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
            <div
              className="relative w-full max-w-2xl h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-1rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] sm:h-auto sm:max-h-[92dvh] ring-2 ring-orange-500 bg-white shadow-2xl flex flex-col animate-in zoom-in-95 duration-500 rounded-lg overflow-hidden"
              style={
                isLegacyTheme
                  ? undefined
                  : {
                      backgroundColor: theme.cartBackgroundColor,
                      boxShadow: `0 0 0 2px ${theme.cartBorderColor}`,
                      color: theme.textColor,
                    }
              }
            >
              <div
                className="px-4 sm:px-8 pt-6 sm:pt-8 pb-4 flex justify-between items-center bg-white shrink-0"
                style={isLegacyTheme ? undefined : { backgroundColor: theme.cartBackgroundColor }}
              >
                <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                  <div>
                    <h2
                      className="text-2xl font-black uppercase tracking-tighter text-gray-900 leading-none"
                      style={isLegacyTheme ? undefined : { color: theme.cartTitleTextColor }}
                    >
                      {t("publicMenu:cart.my_bag")}
                    </h2>

                    <p
                      className="text-[9px] font-black  text-orange-600 uppercase tracking-widest mt-1"
                      style={isLegacyTheme ? undefined : { color: theme.cartDescriptionTextColor }}
                    >
                      {t("publicMenu:cart.items_selected", { count: cart.reduce((s, i) => s + i.quantity, 0) })}
                    </p>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                  <button
                    type="button"
                    onClick={onClose}
                    title={t("publicMenu:cart.close")}
                    aria-label={t("publicMenu:cart.close")}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border-2 border-red-200 bg-red-50 px-3 text-red-700 shadow-sm transition-all hover:border-red-500 hover:bg-red-600 hover:text-white active:scale-95"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.close"
                  >
                    <X size={20} strokeWidth={3} />

                    <span className="hidden text-xs font-black uppercase tracking-[0.12em] sm:inline">
                      {t("publicMenu:cart.close")}
                    </span>
                  </button>
                </Animate>
              </div>

              <div
                className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 space-y-1 no-scrollbar pb-6"
                style={isLegacyTheme ? undefined : { backgroundColor: theme.cartBackgroundColor }}
              >
                {!servicePointSelfService && <AwardAppliedList summary={summary} />}

                {planLimitBlockedMessage && (
                  <div className="bg-amber-50 border border-amber-300 p-3 rounded-lg flex items-start gap-3 mb-2">
                    <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center text-white text-[10px]">
                      ⚠️
                    </div>

                    <p className="text-[12px] font-bold text-amber-800">{planLimitBlockedMessage}</p>
                  </div>
                )}

                {cart.length > 0 && summary.savings > 0 && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                    <div className="bg-emerald-100 border border-emerald-400 p-3 rounded-lg flex items-center gap-3 mb-2">
                      <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-white text-[10px]">
                        💰
                      </div>

                      <p className="text-[12px] font-bold text-emerald-800">
                        {t("publicMenu:cart.promotion_savings", { value: summary.savings.toFixed(2) })}
                      </p>
                    </div>
                  </Animate>
                )}

                {cart.map((item, index) => (
                  <Animate key={index} type={AnimationTypes.slideUpIn} duration="1.8s">
                    <div
                      key={item.uniqueId}
                      className="bg-white border-2 border-gray-200 p-4 rounded-lg shadow-[0_4px_15px_rgba(0,0,0,0.01)] flex flex-col gap-3"
                      style={
                        isLegacyTheme
                          ? undefined
                          : {
                              backgroundColor: theme.surfaceColor,
                              borderColor: theme.cartBorderColor,
                              borderWidth: theme.borderThickness,
                              color: theme.textColor,
                            }
                      }
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative shrink-0">
                          <img
                            src={item.image.toClientRequest()}
                            className="w-14 h-14 rounded-xl bg-white object-fit shadow-sm ring-1 ring-gray-100"
                            alt={item.name}
                          />

                          {item.oldPrice && item.oldPrice > item.price && (
                            <div className="absolute -top-1.5 -left-1.5 bg-red-600 text-white text-[12px] font-black uppercase px-1.5 py-0.5 rounded-md shadow-lg border border-red-500 tracking-tighter">
                              {t("publicMenu:home.offer_badge")}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <h4
                              className="font-black text-gray-900 text-xs uppercase tracking-tight truncate pr-2"
                              style={isLegacyTheme ? undefined : { color: theme.textColor }}
                            >
                              {item.name}
                            </h4>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingItem(item)}
                                className="text-gray-500 rounded-sm mr-2 font-bold hover:text-orange-600 transition-colors"
                                title={t("publicMenu:cart.edit_item")}
                                aria-label={t("publicMenu:cart.edit_item")}
                                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.editItem"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M16.862 4.487a2.25 2.25 0 1 1 3.182 3.182L8.25 19.462 4.5 20.5l1.038-3.75L16.862 4.487Z"
                                  />
                                </svg>
                              </button>

                              <button
                                onClick={() => removeFromCart(item.uniqueId)}
                                className="text-red-500 font-bold hover:text-red-500 transition-colors"
                                data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.removeFromCartItemUniqueId"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {item.selectedAddOns.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {item.selectedAddOns.map((a) => (
                                <span
                                  key={a.id}
                                  className="bg-orange-100 ring-1 ring-orange-300 text-gray-600 text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md border border-gray-100"
                                  style={
                                    isLegacyTheme
                                      ? undefined
                                      : { backgroundColor: theme.categoryBackgroundColor, color: theme.textColor }
                                  }
                                >
                                  + {a.quantity} x {a.name}
                                </span>
                              ))}
                            </div>
                          )}

                          {(item.comboItemsPreview?.length ?? 0) > 0 && (
                            <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50 px-2 py-2">
                              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-orange-700">
                                {t("publicMenu:add_product.combo_items")}
                              </p>

                              <div className="mt-1 space-y-1">
                                {item.comboItemsPreview!.map((comboItem) => (
                                  <p
                                    key={`${item.uniqueId}-${comboItem}`}
                                    className="text-[10px] font-semibold text-slate-700"
                                  >
                                    {comboItem}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {item.selectedSelections.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {item.availableSelections
                            ?.filter((selection) =>
                              item.selectedSelections.some((selected) => selected.selectionId === selection.id),
                            )
                            .map((selection) => {
                              const selectedOptions = item.selectedSelections.filter(
                                (selected) => selected.selectionId === selection.id,
                              )
                              const groupedSelectedOptions = selectedOptions.reduce<
                                { optionId: number; optionName: string; quantity: number; value: number }[]
                              >((groups, selected) => {
                                const existingGroup = groups.find((group) => group.optionId === selected.optionId)

                                if (existingGroup) {
                                  existingGroup.quantity += 1
                                  existingGroup.value += selected.value

                                  return groups
                                }

                                return [
                                  ...groups,
                                  {
                                    optionId: selected.optionId,
                                    optionName: selected.optionName,
                                    quantity: 1,
                                    value: selected.value,
                                  },
                                ]
                              }, [])

                              return (
                                <div
                                  key={`${item.uniqueId}-${selection.id}`}
                                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs font-black uppercase leading-snug text-slate-800">
                                      {selection.name}
                                    </p>

                                    <span className="rounded-md bg-white px-2 py-1 text-[11px] font-black text-slate-600 ring-1 ring-slate-200">
                                      {t("publicMenu:add_product.selected_choices", {
                                        count: selectedOptions.length,
                                      })}
                                    </span>
                                  </div>

                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {buildSelectionStatusMessages(selection, item.selectedSelections, t).map(
                                      (status) => (
                                        <span
                                          key={`${item.uniqueId}-${selection.id}-${status.text}`}
                                          className={`inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-black ${status.className}`}
                                        >
                                          {status.text}
                                        </span>
                                      ),
                                    )}
                                  </div>

                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {groupedSelectedOptions.map((selected) => {
                                      const priceDisplay = ResolveSelectionOptionPriceLabel(
                                        selected.value,
                                        item.price,
                                        companyMemory.zeroValueSelectionOptionDisplayMode,
                                        item.quantity,
                                      )

                                      return (
                                        <span
                                          key={`${item.uniqueId}-${selection.id}-${selected.optionId}`}
                                          className={`rounded-md border px-2 py-1 text-xs font-bold ${
                                            priceDisplay.isFreeStyle
                                              ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                                              : "border-orange-100 bg-orange-50 text-orange-700"
                                          }`}
                                        >
                                          {selected.optionName}
                                          {selected.quantity > 1 ? ` x${selected.quantity}` : ""}{" "}
                                          {priceDisplay.label}
                                        </span>
                                      )
                                    })}
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      )}

                      <div
                        className="flex items-center justify-between border-t border-gray-50 pt-2.5"
                        style={isLegacyTheme ? undefined : { borderColor: theme.cartBorderColor }}
                      >
                        <div className="flex items-center bg-gray-50 px-0.5 py-0.5 rounded-lg border-2 border-gray-100">
                          <button
                            onClick={() => updateCartItemQuantity(item.uniqueId, -1)}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-orange-600 transition-colors"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.updateCartItemQuantityItemUniqueId1"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M20 12H4" />
                            </svg>
                          </button>

                          <span className="w-6 text-center text-xs font-black text-gray-900 tabular-nums">
                            {item.quantity}
                          </span>

                          <button
                            onClick={() => updateCartItemQuantity(item.uniqueId, 1)}
                            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-orange-600 transition-colors"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.updateCartItemQuantityItemUniqueId12"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>

                        <div className="flex flex-col items-end leading-none">
                          {item.oldPrice && item.oldPrice > item.price && (
                            <span className="text-[10px] font-bold text-gray-300 line-through mb-0.5">
                              R$ {(item.oldPrice * item.quantity).toFixed(2)}
                            </span>
                          )}

                          <div
                            className={`text-base font-black tracking-tighter ${item.oldPrice ? "text-red-600" : "text-gray-900"}`}
                            style={isLegacyTheme || item.oldPrice ? undefined : { color: theme.cartTotalValueTextColor }}
                          >
                            R$ {getItemTotal(item).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Animate>
                ))}
              </div>

              <div
                className="px-4 sm:px-5 pt-5 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white border-t border-2 border-gray-200 shrink-0 flex flex-col items-center"
                style={
                  isLegacyTheme
                    ? undefined
                    : { backgroundColor: theme.cartBackgroundColor, borderColor: theme.cartBorderColor }
                }
              >
                <Animate type={AnimationTypes.slideRightIn} duration="1.8s">
                  <div className="flex items-center gap-2 mb-6">
                    <span
                      className="text-[10px] font-black text-gray-400 uppercase tracking-widest"
                      style={isLegacyTheme ? undefined : { color: theme.cartTotalLabelTextColor }}
                    >
                      {t("publicMenu:cart.total")}
                    </span>

                    <div
                      className="text-5xl font-black text-gray-950 tracking-tighter leading-none"
                      style={isLegacyTheme ? undefined : { color: theme.cartTotalValueTextColor }}
                    >
                      R$ {Math.floor(summary.total)}
                      <span className="text-2xl opacity-30">,{(summary.total % 1).toFixed(2).substring(2)}</span>
                    </div>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.slideRightIn} duration="1.5s">
                  <button
                    disabled={cart.length === 0 || !!planLimitBlockedMessage}
                    onClick={() => {
                      if (!companyMemory.canCreatePublicOrder) {
                        setPlanLimitBlockedMessage(t("ID18"))
                        return
                      }

                      if (!auth.customer && !servicePointSelfService) {
                        setCurrentView(EAppView.LoginView)

                        onClose()
                        return
                      }

                      setIsCheckoutOpen(true)
                    }}
                    className="w-full max-w-sm h-14 bg-red-600 text-white rounded-lg font-black text-[11px] uppercase tracking-[0.25em] shadow-2xl active:scale-95 transition-all disabled:opacity-30 disabled:grayscale flex items-center justify-center gap-3"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { backgroundColor: theme.cartButtonColor, color: theme.buttonTextColor }
                    }
                    data-interaction-description-key="interactionDescriptions:publicMenu.pupop.shoppingCartPupop.button.id18"
                  >
                    <span>
                      {planLimitBlockedMessage ? t("publicMenu:cart.paused_orders") : t("publicMenu:cart.pay_now")}
                    </span>

                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </Animate>
              </div>
            </div>
          </div>
        </Animate>
      </div>

      {isCheckoutOpen && (
        <CheckoutPupop
          items={cart}
          onClose={() => setIsCheckoutOpen(false)}
          onSuccess={handleCheckoutSuccess}
          blockedMessage={planLimitBlockedMessage}
          onOrderBlocked={() => {
            setPlanLimitBlockedMessage(t("ID18"))
            setIsCheckoutOpen(false)
          }}
          servicePointSelfService={servicePointSelfService}
        />
      )}

      {editingItem && (
        <AddProductRequestPupop
          product={editingItem}
          initialSelectedAddOns={editingItem.selectedAddOns}
          initialQuantity={editingItem.quantity}
          initialSelectedSelections={editingItem.selectedSelections}
          initialNotes={editingItem.notes ?? ""}
          initialReservationInfo={{
            isReservation: editingItem.isReservation ?? false,
            reservationDate: editingItem.reservationDate,
            reservationTime: editingItem.reservationTime,
          }}
          confirmLabel={t("publicMenu:cart.save_changes")}
          onClose={() => setEditingItem(null)}
          onConfirm={handleEditCartItem}
        />
      )}
    </ModalPortal>
  )
}
